import { PlusOutlined } from "@ant-design/icons";
import { Button, Drawer, Flex, Form, Input, Modal, Radio, Select, Space, Switch, Typography } from "antd";
import { useEffect, useState } from "react";
import CreateDocumentModal from "src/components/CreateDocumentModal/CreateDocumentModal";
import RichTextEditorSlate from "src/components/RichTextEditorSlate";
import { dictionary, i18n } from "src/utils";
import { EMAIL_BODY_TEXT_FIELD } from "src/utils/enums/email-body.enum";
import PreviewSending from "../../Settings/PreviewSending";

const { Title, Text } = Typography;
const { Item } = Form;
const { TextArea } = Input;

const fieldList: EMAIL_BODY_TEXT_FIELD[] = [
  EMAIL_BODY_TEXT_FIELD.EMPLOYER_NAME,
  EMAIL_BODY_TEXT_FIELD.EMPLOYEE_NAME,
  EMAIL_BODY_TEXT_FIELD.ROLE,
  EMAIL_BODY_TEXT_FIELD.WORKLOAD,
  EMAIL_BODY_TEXT_FIELD.SALARY,
  EMAIL_BODY_TEXT_FIELD.BENEFITS,
];

interface EditAdmissionFlowStepProps {
  open: boolean;
  close: () => void;
  data: AdmissionFlowStepPayload;
  setData: React.Dispatch<React.SetStateAction<AdmissionFlowStepPayload>>;
  setSteps: React.Dispatch<React.SetStateAction<AdmissionFlowStepPayload[]>>;
  primaryColor: string;
  secondaryColor: string;
  imageUrl?: string;
  documents: Contract[];
  setDocuments: React.Dispatch<React.SetStateAction<Contract[]>>;
}

export default function EditAdmissionFlowStep({
  open,
  close,
  data,
  setData,
  setSteps,
  primaryColor,
  secondaryColor,
  imageUrl,
  documents,
  setDocuments,
}: EditAdmissionFlowStepProps) {
  const [forceRenderKey, setForceRenderKey] = useState(0);

  // Controla o aparecimento do modal de criação de novo documento;
  const [openCreateDocumentModal, setOpenCreateDocumentModal] = useState(false);

  const [form] = Form.useForm();
  const [isChanged, setIsChanged] = useState(false);

  function parseEmailBody(emailBody: string | any[]): string {
    if (typeof emailBody === "string") {
      return emailBody;
    } else if (Array.isArray(emailBody)) {
      return JSON.stringify(emailBody);
    }

    return "";
  }

  function handleSave() {
    const values = form.getFieldsValue();

    const requiredFields = [];
    if (data.stepId == 7 && data.type === 3) {
      requiredFields.push("fileName");
    }

    if (data.stepId !== 3 && data.type !== 2) {
      requiredFields.push("pagePhrase");
      requiredFields.push("pageDescription");
    }

    if ([2, 4, 6].includes(data.stepId) || (data.stepId == 7 && data.type === 1)) {
      requiredFields.push("documentId");
    }

    const emptyFields = requiredFields.filter(
      (field) => values[field] === null || values[field] === undefined || values[field] === "",
    );

    if (emptyFields.length > 0) {
      Modal.warning({
        title: i18n(dictionary.requiredNotFilled),
        content: i18n(dictionary.fillRequired, {
          emptyFields: emptyFields
            .map((field) => {
              switch (field) {
                case "documentId":
                  return i18n(
                    dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.document.select[
                      data.stepId as StepWithDocumentAndValidation
                    ],
                  );
                case "fileName":
                  return i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.fileName);
                case "pagePhrase":
                  return i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.phrase.label);
                case "pageDescription":
                  return i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.description.label);
              }
            })
            .join(", "),
        }),
      });
      return;
    }

    setSteps((prevSteps) => {
      let tempSteps = [...prevSteps];
      tempSteps[data.index] = { ...data, ...values, emailBody: parseEmailBody(values.emailBody), new: false };
      return tempSteps;
    });

    form.resetFields();
    close();
  }

  function handleClose() {
    if (isChanged) {
      const confirm = {
        title: i18n(dictionary.message.unsavedChanges),
        content: i18n(dictionary.message.exitWithoutSaving),
        okText: i18n(dictionary.action.yes),
        cancelText: i18n(dictionary.action.no),
        confirmLoading: true,
        onOk: () => {
          form.resetFields();
          close();
        },
        onCancel() {
          return;
        },
        closable: false,
      };
      Modal.confirm(confirm);
    } else {
      form.resetFields();
      close();
    }
  }

  useEffect(() => {
    // Força renderização do componente RichTextEditorSlate
    if (open) {
      setForceRenderKey((prevKey) => prevKey + 1);
      form.setFieldsValue(data);
      setIsChanged(data.new ?? false);
    }
  }, [open]);

  return (
    <>
      <CreateDocumentModal
        open={openCreateDocumentModal}
        onCancel={() => setOpenCreateDocumentModal(false)}
        setDocuments={setDocuments}
        setData={setData}
      />
      <Drawer
        width={600}
        open={open}
        onClose={handleClose}
        title={
          <Flex
            align="center"
            justify="center"
            gap={10}
          >
            <Text>
              {i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.label)}&nbsp;
              {Number(data.index + 1)}
            </Text>
            <Text type="secondary">{"➝"}</Text>
            <Title
              style={{ margin: 0, color: "#884ea6" }}
              level={5}
            >
              {data.stepId === 7
                ? data.stepName
                : i18n(dictionary.pages.admissionFlowCreate.tabs.t1.cards.others.titles[data.stepId])}
            </Title>
          </Flex>
        }
      >
        <Form
          form={form}
          onValuesChange={(changedValues, allValues) => {
            setIsChanged(true);
          }}
          layout="vertical"
        >
          {/* Configuração específica do passo livre */}
          {data.stepId === 7 && (
            <>
              {/* Nome do passo */}
              <Item
                name="stepName"
                label={i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.name)}
              >
                <Input
                  maxLength={50}
                  onChange={({ target }) => {
                    setData((prevData) => ({ ...prevData, stepName: target.value }));
                  }}
                  value={data.stepName}
                />
              </Item>
            </>
          )}
          {/* Assunto do e-mail */}
          <Item
            name="emailSubject"
            label={i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.subject)}
          >
            <Input
              maxLength={50}
              onChange={({ target }) => setData((prevData) => ({ ...prevData, emailSubject: target.value }))}
              value={data.emailSubject}
            />
          </Item>
          {/* Texto do e-mail */}
          <Item
            name="emailBody"
            label={i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.text)}
          >
            <RichTextEditorSlate
              key={forceRenderKey}
              content={JSON.parse(data.emailBody)}
              fieldList={fieldList}
              onChange={(value) =>
                setData((prevData) => ({
                  ...prevData,
                  emailBody: JSON.stringify(value),
                }))
              }
              placeHolder={""}
              selectWithMarginTop
            />
          </Item>
          {/* Configuração específica do passo livre */}
          {data.stepId === 7 && (
            <>
              {/* Tipo de passo */}
              <Item
                name="type"
                label={i18n(dictionary.label.itemType, { item: i18n(dictionary.entity.step) })}
              >
                <Radio.Group
                  value={data.type}
                  onChange={({ target }) => setData((prevData) => ({ ...prevData, type: target.value }))}
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <Radio value={1}>{i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.type[1])}</Radio>
                  <Radio value={2}>{i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.type[2])}</Radio>
                  <Radio value={3}>{i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.type[3])}</Radio>
                </Radio.Group>
              </Item>
            </>
          )}
          {([2, 4, 6].includes(data.stepId) || (data.stepId == 7 && data.type === 1)) && (
            <>
              {/* Forma de assinatura */}
              <Item
                name="validationType"
                label={i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.validationType.label)}
              >
                <Radio.Group
                  value={data.validationType}
                  onChange={({ target }) => setData((prevData) => ({ ...prevData, validationType: target.value }))}
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <Radio value={1}>
                    {i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.validationType[1])}
                  </Radio>
                  <Radio value={2}>
                    {i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.validationType[2])}
                  </Radio>
                </Radio.Group>
              </Item>
              {/* Documento */}
              <Item
                name="documentId"
                label={i18n(
                  dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.document.select[
                    data.stepId as StepWithDocumentAndValidation
                  ],
                )}
                rules={[{ required: true, message: i18n(dictionary.required) }]}
              >
                <Flex gap={10}>
                  <Select
                    value={data.documentId}
                    options={documents.map((option) => ({
                      value: option.id,
                      label: option.description,
                    }))}
                    placeholder={i18n(dictionary.selectDocument)}
                    onChange={(value) => {
                      form.setFieldsValue({ documentId: value });
                      setData((prev) => ({ ...prev, documentId: value }));
                    }}
                  />

                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setOpenCreateDocumentModal(true)}
                  >
                    {i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.document.button)}
                  </Button>
                </Flex>
              </Item>
            </>
          )}
          {/* Configuração específica do passo livre */}
          {data.stepId == 7 && data.type === 3 && (
            <>
              {/* Nome do arquivo/documento */}
              <Item
                name="fileName"
                label={i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.fileName)}
                rules={[{ required: true, message: i18n(dictionary.required) }]}
              >
                <Input
                  maxLength={50}
                  onChange={({ target }) => {
                    setData((prevData) => ({ ...prevData, fileName: target.value }));
                  }}
                  value={data.fileName}
                />
              </Item>
            </>
          )}
          {data.stepId !== 3 && data.type !== 2 && (
            <>
              {/* Frase da landing page */}
              <Item
                name="pagePhrase"
                label={i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.phrase.label)}
                rules={[{ required: true, message: i18n(dictionary.required) }]}
              >
                <Input
                  maxLength={255}
                  onChange={({ target }) => setData((prevData) => ({ ...prevData, pagePhrase: target.value }))}
                  value={data.pagePhrase}
                />
              </Item>
              {/* Descrição da landing page */}
              <Item
                name="pageDescription"
                label={i18n(dictionary.pages.admissionFlowCreate.tabs.t1.step.fields.description.label)}
                rules={[{ required: true, message: i18n(dictionary.required) }]}
              >
                <TextArea
                  style={{ resize: "none", height: 62 }}
                  maxLength={100}
                  onChange={({ target }) => setData((prevData) => ({ ...prevData, pageDescription: target.value }))}
                  value={data.pageDescription}
                />
              </Item>
              {/* Enviar por whatsapp */}
              <Item label={i18n(dictionary.label.sendToWhatsApp)}>
                <Switch
                  checkedChildren={i18n(dictionary.label.yes)}
                  unCheckedChildren={i18n(dictionary.label.no)}
                  value={data.sendByWhatsapp}
                  onChange={(value) => {
                    setData((prevData) => ({ ...prevData, sendByWhatsapp: value }));
                  }}
                />
              </Item>
              {/* Preview da landing page */}
              <div style={{ border: "1px solid #d9d9d9", marginTop: 20 }}>
                <PreviewSending
                  logoCompany={imageUrl}
                  principalColor={primaryColor}
                  secondaryColor={secondaryColor}
                  frase={data.pagePhrase}
                  description={data.pageDescription}
                  optionPreview="medium"
                />
              </div>
            </>
          )}
          <Form.Item>
            <Space style={{ marginTop: 20 }}>
              <Button
                type="primary"
                htmlType="submit"
                onClick={handleSave}
              >
                {i18n(dictionary.action.save)}
              </Button>
              <Button
                htmlType="button"
                onClick={handleClose}
              >
                {i18n(dictionary.action.cancel)}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
}
